import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Inbox, CheckCircle2, XCircle, ExternalLink, Clock, GitBranch, FolderKanban, ShieldCheck, ClipboardCheck,
} from 'lucide-react';
import api from '../lib/api';
import { fmtRelative } from '../lib/format';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

// Visual identity per approval type. New types fall back to a neutral look.
const TYPE_META = {
  revision: { icon: GitBranch, tone: 'copper', label: 'Budget Revision' },
  tender: { icon: FolderKanban, tone: 'teal', label: 'Tender Approval' },
  cc_opening: { icon: ClipboardCheck, tone: 'brand', label: 'Cost Centre Opening' },
};

export default function Approvals() {
  const navigate = useNavigate();
  const [data, setData] = useState(null); // { items, count, types, projects }
  const [typeFilter, setTypeFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [action, setAction] = useState(null); // { item, mode: 'approve'|'reject' }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get('/approvals/inbox');
      setData(res);
    } catch {
      setData({ items: [], count: 0, types: [], projects: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAction = (item, mode) => { setNote(''); setAction({ item, mode }); };

  const submitAction = async () => {
    if (!action) return;
    const { item, mode } = action;
    const spec = mode === 'approve' ? item.approve : item.reject;
    if (!spec) return;
    if (mode === 'reject' && !note.trim()) { toast.error('Please add a reason for rejection.'); return; }
    setBusy(true);
    try {
      const body = { ...(spec.body || {}), note: note.trim() };
      await api.post(spec.endpoint, body);
      toast.success(mode === 'approve' ? 'Approved' : 'Rejected');
      setAction(null);
      // Notify the shell to refresh its badge.
      window.dispatchEvent(new Event('approvals:changed'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${mode} — it may no longer be your turn.`);
      // Refresh so a stale row disappears.
      load();
    } finally {
      setBusy(false);
    }
  };

  const items = data?.items || [];
  const filtered = items.filter((i) => {
    if (typeFilter !== 'all' && i.type !== typeFilter) return false;
    if (projectFilter !== 'all' && (i.projectCode || i.projectId) !== projectFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8" data-testid="approvals-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">My Approvals</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Everything awaiting your decision, across every workflow. Acting here goes through the same checks as the item itself.
          </p>
        </div>
        <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-sm" data-testid="approvals-total-count">
          <Inbox size={14} />{items.length} pending
        </Badge>
      </div>

      {/* Filters */}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]" data-testid="approvals-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {(data?.types || []).map((t) => (
                <SelectItem key={t} value={t} data-testid={`approvals-type-opt-${t}`}>{TYPE_META[t]?.label || t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-[200px]" data-testid="approvals-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {(data?.projects || []).map((p) => (
                <SelectItem key={p} value={p} data-testid={`approvals-project-opt-${p}`}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* List */}
      {data === null ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="erak-card grid place-items-center gap-2 p-12 text-center" data-testid="approvals-empty">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]">
            <CheckCircle2 size={24} />
          </div>
          <div className="text-sm font-medium">You&apos;re all caught up</div>
          <p className="max-w-sm text-xs text-muted-foreground">
            {items.length === 0 ? 'Nothing is awaiting your approval right now.' : 'No items match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="approvals-list">
          {filtered.map((item) => {
            const meta = TYPE_META[item.type] || { icon: ShieldCheck, tone: 'brand', label: item.typeLabel || item.type };
            const Icon = meta.icon;
            return (
              <div key={item.key} data-testid={`approval-row-${item.key}`}
                className="erak-card flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <div className={cn('mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg',
                    `bg-[hsl(var(--${meta.tone})/0.12)] text-[hsl(var(--${meta.tone}))]`)}>
                    <Icon size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{meta.label}</Badge>
                      <span className="font-num text-xs text-[hsl(var(--copper))]">{item.projectCode}</span>
                      <span className="truncate text-sm font-semibold">{item.title}</span>
                      {item.isAdminOverride && (
                        <Badge variant="secondary" className="gap-1 text-[10px] text-[hsl(var(--warning))]"><ShieldCheck size={9} />Admin override</Badge>
                      )}
                    </div>
                    <div className="mt-1 truncate text-xs text-muted-foreground">{item.summary}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1 font-medium text-foreground/80">{item.stageLabel}</span>
                      {item.raisedBy && <span>Raised by {item.raisedBy}</span>}
                      {item.raisedAt && <span className="inline-flex items-center gap-1"><Clock size={11} />{fmtRelative(item.raisedAt)}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2 md:justify-end">
                  <Button size="sm" variant="ghost" className="h-8" data-testid={`approval-open-${item.key}`}
                    onClick={() => navigate(item.openPath)}>
                    <ExternalLink size={14} className="mr-1.5" />Open
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]"
                    data-testid={`approval-reject-${item.key}`} onClick={() => openAction(item, 'reject')}>
                    <XCircle size={14} className="mr-1.5" />Reject
                  </Button>
                  <Button size="sm" className="h-8" data-testid={`approval-approve-${item.key}`}
                    onClick={() => openAction(item, 'approve')}>
                    <CheckCircle2 size={14} className="mr-1.5" />{item.approveLabel || 'Approve'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Approve / Reject dialog */}
      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent data-testid="approval-action-dialog">
          <DialogHeader>
            <DialogTitle>{action?.mode === 'approve' ? 'Approve' : 'Reject'} — {action?.item?.projectCode} {action?.item?.title}</DialogTitle>
          </DialogHeader>
          {action && (
            <div className="space-y-3">
              <div className="rounded-lg border bg-muted/30 p-3 text-xs">
                <div className="font-medium">{action.item.summary}</div>
                <div className="mt-1 text-muted-foreground">{action.item.stageLabel}</div>
              </div>
              <div className="space-y-1.5">
                <Label>Note {action.mode === 'reject' ? '(required)' : '(optional)'}</Label>
                <Textarea rows={3} data-testid="approval-note-input" value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={action.mode === 'approve' ? 'Add an optional note…' : 'Why is this being rejected?'} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="approval-action-confirm" onClick={submitAction} disabled={busy}
              className={action?.mode === 'reject' ? 'bg-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/90' : ''}>
              {busy ? 'Working…' : (action?.mode === 'approve' ? (action?.item?.approveLabel || 'Approve') : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
